import { useEffect, useState } from 'react';

type Kind = 'gmail' | 'health';

interface MonitoringRun {
  id: string;
  kind: Kind;
  status: 'running' | 'done' | 'failed';
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  total_checked: number;
  new_flagged: number;
  still_flagged: number;
  cleared: number;
  errors: number;
  error_message: string | null;
}

interface Finding {
  id: string;
  kind: Kind;
  email: string;
  status: string;
  sender: string | null;
  snippet: string | null;
  first_detected_at: string;
  last_seen_at: string;
  cleared_at: string | null;
  dismissed_by: string | null;
}

interface RunDetailResponse {
  run: MonitoringRun;
  newFindings: Finding[];
  reConfirmed: Finding[];
  clearedByThisRun: Finding[];
}

interface Props {
  runId: string;
  kind: Kind;
  onClose: () => void;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '-';
}

export function MonitoringRunDetail({ runId, kind, onClose }: Props): JSX.Element {
  const [data, setData] = useState<RunDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    fetch(`/api/monitoring/runs/${runId}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (!j.success) throw new Error(j.error?.message ?? 'load failed');
        setData(j.data);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="monitoring-rundetail-backdrop" onClick={onClose}>
      <div
        className="monitoring-rundetail-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="monitoring-rundetail-close"
          aria-label="Close"
          onClick={onClose}
        >
          X
        </button>

        {error && <div className="monitoring-error">{error}</div>}

        {!data && !error && <p className="monitoring-empty">Loading...</p>}

        {data && (
          <>
            <h2 className="monitoring-rundetail-title">
              Run {data.run.id.slice(0, 8)} - {data.run.kind}
            </h2>

            <dl className="monitoring-rundetail-meta">
              <dt>Status</dt>
              <dd>{data.run.status}</dd>

              <dt>Triggered by</dt>
              <dd>{data.run.triggered_by}</dd>

              <dt>Started</dt>
              <dd>{fmtDate(data.run.started_at)}</dd>

              <dt>Finished</dt>
              <dd>{fmtDate(data.run.finished_at)}</dd>

              <dt>Checked</dt>
              <dd>{data.run.total_checked}</dd>

              <dt>New flagged</dt>
              <dd>{data.run.new_flagged}</dd>

              <dt>Still flagged</dt>
              <dd>{data.run.still_flagged}</dd>

              {kind === 'health' && (
                <>
                  <dt>Cleared</dt>
                  <dd>{data.run.cleared}</dd>
                </>
              )}

              <dt>Errors</dt>
              <dd>{data.run.errors}</dd>

              {data.run.error_message && (
                <>
                  <dt>Error</dt>
                  <dd>{data.run.error_message}</dd>
                </>
              )}
            </dl>

            <h3 className="monitoring-rundetail-section-title">
              New findings introduced ({data.newFindings.length})
            </h3>
            {data.newFindings.length === 0 ? (
              <p className="monitoring-empty">None.</p>
            ) : (
              <ul className="monitoring-rundetail-list">
                {data.newFindings.map((f) => (
                  <li key={f.id}>
                    <strong>{f.email}</strong>
                    <span className="monitoring-rundetail-status"> - {f.status}</span>
                    {kind === 'gmail' && f.sender && (
                      <em className="monitoring-rundetail-sender"> from {f.sender}</em>
                    )}
                    <div className="monitoring-rundetail-detected">
                      first detected {fmtDate(f.first_detected_at)}
                    </div>
                    {kind === 'gmail' && f.snippet && (
                      <div className="monitoring-rundetail-snippet">{f.snippet}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {kind === 'health' && (
              <>
                <h3 className="monitoring-rundetail-section-title">
                  Findings cleared by this run ({data.clearedByThisRun.length})
                </h3>
                {data.clearedByThisRun.length === 0 ? (
                  <p className="monitoring-empty">None.</p>
                ) : (
                  <ul className="monitoring-rundetail-list">
                    {data.clearedByThisRun.map((f) => (
                      <li key={f.id}>
                        <strong>{f.email}</strong>
                        <div className="monitoring-rundetail-detected">
                          cleared {fmtDate(f.cleared_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
